import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

import IconButton from "@mui/material/IconButton";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import ViewWeekOutlinedIcon from "@mui/icons-material/ViewWeekOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ReorderIcon from "@mui/icons-material/Reorder";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { setLocalStorageData } from "./reviewColumnUtils";
import APP_CONSTANTS from "../../../utils/config";
import { Skeleton } from "@mui/material";

export function buildFocusModeVisible(key_ref_columns, allowed) {
  return new Set([...key_ref_columns, ...allowed]);
}

export function buildAllVisible(columns) {
  return new Set(columns.map((c) => c.key));
}

function categoryFor(key, reference_columns, allowedSet, theme) {
  if (reference_columns.includes(key))
    return {
      label: "Key",
      color: theme.palette.brain.blue.text,
      bg: theme.palette.brain.blue.background,
    };
  if (allowedSet.has(key))
    return {
      label: "Editable",
      color: theme.palette.brain.green.text,
      bg: theme.palette.brain.green.background,
    };
  return {
    label: "Restricted",
    color: theme.palette.brain.grey.text,
    bg: theme.palette.brain.grey.background,
  };
}

function SortableRow({
  col,
  index,
  allowedSet,
  onHide,
  theme,
  reference_columns,
  readOnly,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.key });
  const cat = categoryFor(col.key, reference_columns, allowedSet, theme);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        mb: 0.5,
        borderRadius: 1.25,
        backgroundColor: theme.palette.brain.drawer.item_background,
        border: "1px solid",
        borderColor: theme.palette.brain.drawer.item_border,
        transition:
          "background-color 0.15s, border-color 0.15s, box-shadow 0.15s",
        "&:hover": {
          borderColor: theme.palette.brain.drawer.item_hover_border,
          backgroundColor: theme.palette.brain.drawer.item_hover_background,
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.brain.blue.text,
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
          "&:hover": { color: theme.palette.brain.drawer.item_hover_border },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18 }} />
      </Box>
      <Typography
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.65rem",
          fontWeight: 700,
          color: theme.palette.brain.blue.text,
          backgroundColor: theme.palette.brain.blue.background,
        }}
      >
        {index + 1}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 600,
          color: theme.palette.brain.drawer.text_primary,
          flex: 1,
        }}
      >
        {col.label}
      </Typography>
      <Chip
        label={cat.label}
        size="small"
        sx={{
          height: 18,
          fontSize: "0.6rem",
          fontWeight: 700,
          backgroundColor: cat.bg,
          color: cat.color,
          border: "none",
        }}
      />
      {(cat.label !== "Editable" || readOnly) && (
        <IconButton
          size="small"
          onClick={() => onHide(col.key)}
          sx={{
            p: 0.25,
            color: theme.palette.brain.drawer.text_primary,
            "&:hover": { color: theme.palette.brain.red.text },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
}

export default function ColumnVisibilityControl({
  columns,
  allowedColumns,
  keyRefColumns = [],
  refColumns = [],
  visible,
  onChange,
  theme,
  columnOrder = [],
  onReorder = () => {},
  loading = false,
  readOnly = false,
  visibleColumnKey = APP_CONSTANTS.VISIBLE_COLUMN,
  columnOrderKey = APP_CONSTANTS.COLUMN_ORDER,
}) {
  const [open, setOpen] = useState(false);
  const allowedSet = useMemo(() => new Set(allowedColumns), [allowedColumns]);

  const editable = useMemo(
    () => columns.filter((c) => allowedSet.has(c.key)),
    [allowedSet, columns]
  );
  const nonEditable = useMemo(
    () => columns.filter((c) => refColumns.includes(c.key)),
    [columns]
  );
  const others = useMemo(
    () =>
      columns.filter(
        (c) => !refColumns.includes(c.key) && !allowedSet.has(c.key)
      ),
    [columns, allowedSet]
  );

  const hiddenCount = columns.length - visible.size;

  // Selected columns in current order
  const selectedOrdered = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return columnOrder
      .filter((k) => visible.has(k))
      .map((k) => byKey.get(k))
      .filter(Boolean);
  }, [columns, columnOrder, visible]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = selectedOrdered.map((c) => c.key);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reorderedSelected = arrayMove(ids, oldIndex, newIndex);
    const result = [];
    let i = 0;
    for (const k of columnOrder) {
      if (visible.has(k)) {
        result.push(reorderedSelected[i++]);
      } else {
        result.push(k);
      }
    }
    columns.forEach((c) => {
      if (!result.includes(c.key)) result.push(c.key);
    });
    onReorder(result);
    setLocalStorageData(columnOrderKey, result);
  };

  const toggle = (key) => {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    if (!readOnly) editable.forEach((c) => next.add(c.key));
    onChange(next);
    setLocalStorageData(visibleColumnKey, [...next]);
  };

  const toggleGroup = (cols, on) => {
    const next = new Set(visible);
    cols.forEach((c) => {
      if (on) next.add(c.key);
      else next.delete(c.key);
    });
    if (!readOnly) editable.forEach((c) => next.add(c.key));
    onChange(next);
    setLocalStorageData(visibleColumnKey, [...next]);
  };

  const groupState = (cols) => {
    const total = cols.length;
    const on = cols.filter((c) => visible.has(c.key)).length;
    return {
      total,
      on,
      all: total > 0 && on === total,
      some: on > 0 && on < total,
    };
  };

  const focus = () => {
    const focus_columns = buildFocusModeVisible(keyRefColumns, allowedColumns);
    setLocalStorageData(visibleColumnKey, [...focus_columns]);
    onChange(focus_columns);
  };
  const all = () => {
    const all_columns = buildAllVisible(columns);
    setLocalStorageData(visibleColumnKey, [...all_columns]);
    onChange(all_columns);
  };

  const Section = ({ title, icon, cols, locked, accent }) => {
    const st = groupState(cols);
    return (
      <Box sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
            px: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: `${accent}14`,
                color: accent,
              }}
            >
              {icon}
            </Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: theme.palette.brain.table.header.text,
              }}
            >
              {title}
            </Typography>
            <Chip
              label={`${st.on}/${st.total}`}
              size="small"
              sx={{
                height: 16,
                fontSize: "0.6rem",
                fontWeight: 700,
                bgcolor: `${accent}14`,
                color: accent,
              }}
            />
          </Box>
          {!locked && cols.length > 0 && (
            <Checkbox
              size="small"
              checked={st.all}
              indeterminate={st.some}
              onChange={(e) => toggleGroup(cols, e.target.checked)}
              sx={{
                p: 0.25,
                color: accent,
                "&.Mui-checked": { color: accent },
                "&.MuiCheckbox-indeterminate": { color: accent },
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.25,
            pl: 0.5,
            borderLeft: `2px solid ${accent}33`,
            ml: 0.5,
          }}
        >
          {cols.length === 0 && (
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: theme.palette.brain.table.header.text,
                fontStyle: "italic",
                gridColumn: "1 / -1",
                py: 0.5,
              }}
            >
              None
            </Typography>
          )}
          {cols.map((c) => (
            <FormControlLabel
              key={c.key}
              control={
                <Checkbox
                  size="small"
                  checked={visible.has(c.key)}
                  disabled={locked}
                  onChange={() => toggle(c.key)}
                  sx={{
                    p: 0.25,
                    color: accent,
                    "&.Mui-checked": { color: accent },
                    "&.Mui-disabled": {
                      color: `${accent} !important`,
                      opacity: 0.85,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: locked
                      ? theme.palette.brain.table.row.text
                      : theme.palette.brain.table.header.text,
                    fontWeight: 500,
                  }}
                >
                  {c.label}
                </Typography>
              }
              sx={{ m: 0, py: 0.15 }}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <>
      {loading ? (
        <Skeleton
          variant="rounded"
          sx={{ minWidth: "230px", height: "30px" }}
        />
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
          onClick={() => setOpen(true)}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            borderColor: theme.palette.brain.yellow.text,
            color: theme.palette.brain.table.row.text,
            "&:hover": {
              borderColor: theme.palette.brain.yellow.text,
              color: theme.palette.brain.yellow.text,
            },
          }}
        >
          Customize Columns
          {hiddenCount > 0 && (
            <Chip
              label={`${hiddenCount} hidden`}
              size="small"
              sx={{
                ml: 0.75,
                height: 16,
                fontSize: "0.6rem",
                fontWeight: 700,
                bgcolor: theme.palette.brain.blue.background,
                color: theme.palette.brain.blue.text,
              }}
            />
          )}
        </Button>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          zIndex: 12000,
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            backgroundColor: theme.palette.brain.drawer.background,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background: theme.palette.brain.table.header.background,
            color: theme.palette.brain.table.header.text,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.2 }}
            >
              Customize Columns
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", opacity: 0.85, mt: 0.25 }}>
              Reorder, show or hide columns in the table
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{ color: theme.palette.brain.table.header.text }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Presets */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.brain.grey.border}`,
            backgroundColor: theme.palette.brain.table.header.background,
          }}
        >
          <Button
            size="small"
            variant="contained"
            startIcon={<CenterFocusStrongOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={focus}
            sx={{
              textTransform: "none",
              fontSize: "0.72rem",
              fontWeight: 700,
              flex: 1,
              color: theme.palette.button.text,
              background: theme.palette.secondary.main2,
              "&:hover": {
                background: theme.palette.secondary.main2,
              },
            }}
          >
            Focus Mode
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewWeekOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={all}
            sx={{
              textTransform: "none",
              fontSize: "0.72rem",
              fontWeight: 700,
              flex: 1,
              borderColor: theme.palette.secondary.main2,
              color: theme.palette.brain.table.header.text,
              "&:hover": {
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
              },
            }}
          >
            Show All
          </Button>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {/* Selected (sortable) */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: theme.palette.secondary.main2,
                  color: "#fff",
                }}
              >
                <ReorderIcon sx={{ fontSize: 15 }} />
              </Box>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  color: theme.palette.brain.table.row.text,
                }}
              >
                Selected Columns
              </Typography>
              <Chip
                label={selectedOrdered.length}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: theme.palette.brain.blue.background,
                  color: theme.palette.brain.blue.text,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "0.68rem",
                color: theme.palette.brain.table.row.text,
                mb: 1,
                ml: 0.25,
              }}
            >
              Drag to reorder columns — order updates the table instantly
            </Typography>

            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: theme.palette.brain.drawer.dnd_background,
                border: `1px dashed ${theme.palette.brain.drawer.dnd_border}`,
                minHeight: 80,
              }}
            >
              {selectedOrdered.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: theme.palette.brain.grey.text,
                    fontStyle: "italic",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  No columns selected
                </Typography>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedOrdered.map((c) => c.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {selectedOrdered.map((col, idx) => (
                      <SortableRow
                        key={col.key}
                        col={col}
                        index={idx}
                        allowedSet={allowedSet}
                        onHide={toggle}
                        theme={theme}
                        reference_columns={refColumns}
                        readOnly={readOnly}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </Box>
          </Box>

          <Divider
            sx={{
              my: 2,
              "&::before, &::after": {
                borderColor: theme.palette.brain.grey.border,
              },
            }}
          >
            <Chip
              label="Show / Hide by Group"
              size="small"
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: theme.palette.brain.yellow.text,
                backgroundColor: theme.palette.brain.yellow.background,
              }}
            />
          </Divider>
          {editable?.length !== 0 && (
            <>
              <Section
                title="Your Editable Fields"
                icon={<EditNoteOutlinedIcon sx={{ fontSize: 14 }} />}
                cols={editable}
                locked={!readOnly}
                accent={theme.palette.brain.grey.text}
              />
              <Divider
                sx={{ my: 1, borderColor: theme.palette.brain.grey.border }}
              />
            </>
          )}
          <Section
            title="Non-Editable Fields"
            icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
            cols={nonEditable}
            accent={theme.palette.brain.blue.text}
          />

          {others?.length !== 0 && (
            <>
              <Divider
                sx={{ my: 1, borderColor: theme.palette.brain.grey.border }}
              />
              <Section
                title="Other Reviewers' Fields"
                icon={<GroupOutlinedIcon sx={{ fontSize: 14 }} />}
                cols={others}
                accent={theme.palette.brain.blue.text}
              />
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
